import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
} from '@angular/core';
import {
  ToastNotificationComponent,
  ToastPosition,
  ToastType,
} from '@app/shared/components/toast-notification/toast-notification.component';

export interface ToastOptions {
  message: string;
  title?: string;
  type?: ToastType;
  position?: ToastPosition;
  duration?: number;
  autoDismiss?: boolean;
}

/**
 * ToastService — dynamically creates and removes ToastNotificationComponent instances.
 *
 * Follows the same ApplicationRef + createComponent pattern used by ModalService
 * so it can be called from interceptors and other non-view contexts.
 *
 * @example
 * toastService.show({ type: 'error', message: 'Error del servidor.' });
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _appRef = inject(ApplicationRef);
  private readonly _injector = inject(EnvironmentInjector);

  show(options: ToastOptions): void {
    const ref = this._createToast(options);
    document.body.appendChild(ref.location.nativeElement as HTMLElement);
    this._appRef.attachView(ref.hostView);

    ref.instance.dismiss.subscribe(() => this._destroy(ref));
  }

  showError(message: string, title = 'Ha ocurrido un error'): void {
    this.show({ type: 'error', message, title });
  }

  showSuccess(message: string, title?: string): void {
    this.show({ type: 'success', message, title });
  }

  showWarning(message: string, title?: string): void {
    this.show({ type: 'warning', message, title });
  }

  private _createToast(options: ToastOptions): ComponentRef<ToastNotificationComponent> {
    const ref = createComponent(ToastNotificationComponent, {
      environmentInjector: this._injector,
    });

    const i = ref.instance;
    i.type = options.type ?? 'info';
    i.message = options.message;
    i.title = options.title ?? '';
    i.position = options.position ?? 'top-right';
    i.duration = options.duration ?? 5000;
    i.autoDismiss = options.autoDismiss ?? true;
    i.visible = true;

    return ref;
  }

  private _destroy(ref: ComponentRef<ToastNotificationComponent>): void {
    this._appRef.detachView(ref.hostView);
    (ref.location.nativeElement as HTMLElement).remove();
    ref.destroy();
  }
}
